import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import appleIcon from '~/static/assets/icons/landing-page/apple_icon.svg';
import burgerIcon from '~/static/assets/icons/landing-page/burger-icon.svg';
import chevronDownIcon from '~/static/assets/icons/landing-page/chevron-down-icon.svg';
import googlePlayIcon from '~/static/assets/icons/landing-page/google_play.svg';
import logoutIcon from '~/static/assets/icons/landing-page/logout-icon.svg';
import windowsIcon from '~/static/assets/icons/landing-page/windows_icon.svg';
// Asset imports - bundler handles path resolution
import headerLogo from '~/static/assets/logos/headerLogo.png';
import headerLogoDark from '~/static/assets/logos/headerLogoDark.svg';
import { useUserForDisplay } from '../../hooks/useUserData';
import { type AppDownloadIcon, getAppDownloadLinks } from '../../lib/appDownloads';
import { authRedirectService } from '../../services/authRedirectService';
import type { UserInfo } from '../../types/user';

const APP_DOWNLOAD_ICONS: Record<AppDownloadIcon, ImageAsset> = {
  apple: appleIcon,
  windows: windowsIcon,
  googlePlay: googlePlayIcon,
};

interface HeaderProps {
  onLogout?: () => void;
  onLogin?: () => void;
  onOpenSettings?: () => void;
  /**
   * Personal Meetings entry-point, rendered next to Settings. SM-1273: this
   * is the standalone-only entry-point — the header is not rendered in MF
   * (TeamHub-hosted) mode, which uses the HomeControlsPanel link instead.
   */
  onOpenPersonalMeetings?: () => void;
  userInfo?: UserInfo | null;
  isAuthenticated?: boolean;
  // If true, will use global user context instead of props
  useGlobalUser?: boolean;
}

const _getUserGravatar = async (address: string): Promise<Blob | null> => {
  if (window.crypto?.subtle) {
    const size = 32; // 32px for header avatar

    console.debug(`[gravatar] fetching for ${address}...`);

    const hashHex = await crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(address))
      .then((hashBuffer) => {
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      });

    // NOTE: we can't just return the gravatar here URL since the user might not have configured
    // NOTE: a profile picture, and the image would be empty.
    // NOTE: therefore, we fetch it first and make it into a blob if it succeeds.

    try {
      const response = await fetch(`https://www.gravatar.com/avatar/${hashHex}?s=${size}&d=404`);

      if (!response.ok) {
        if (response.status === 404) {
          console.info('[gravatar] not configured (not found)');
        } else {
          console.error(`failed to fetch user gravatar (code=${response.status})`);
        }
        return null;
      }

      const data = await response.blob();
      console.debug(`[gravatar] caching data for ${address}...`);
      return data;
    } catch (e) {
      if (e instanceof Error) {
        console.warn(`[gravatar] fetch failed: ${e.message}`);
      }
      return null;
    }
  }

  console.info('[gravatar] no crypto support, skipping');
  return null;
};

let _gravatarAddress: string | null = null;
let _gravatarPromise: Promise<Blob | null> = new Promise((resolve, _reject) => {
  resolve(null);
});

const getUserGravatar = async (userEmail: string): Promise<Blob | null> => {
  const address = String(userEmail).trim().toLowerCase();

  if (_gravatarAddress === address) {
    console.debug(`[gravatar] waiting for ongoing request for ${address}...`);
    const result = await _gravatarPromise;
    console.debug(`[gravatar] request done, returning..`);
    return result;
  }

  _gravatarAddress = address;
  _gravatarPromise = _getUserGravatar(userEmail);
  return await _gravatarPromise;
};

// Get user avatar/photo - support multiple OAuth providers
// Google: picture
// Microsoft: picture, photo, or graph API fields
// CommUnity: avatar, profile_picture, image, photo
// Switchvox: avatar, profile_picture, image, photo

const isValidAvatarUrl = (data: string | undefined) => {
  if (!data) {
    return null;
  }
  try {
    return new URL(data);
  } catch (_e) {
    console.debug(`invalid avatar url: ${data}`);
    return null;
  }
};

const getUserAvatar = async (
  userInfo: UserInfo | null | undefined,
  userEmail: string | undefined,
): Promise<[string, string | null]> => {
  // biome-ignore-start lint/style/noUselessElse: meh
  if (isValidAvatarUrl(userInfo?.picture)) {
    // Google, Microsoft standard
    return ['picture', userInfo?.picture || null];
  } else if (isValidAvatarUrl(userInfo?.photo)) {
    // Microsoft alternative
    return ['photo', userInfo?.photo || null];
  } else if (isValidAvatarUrl(userInfo?.avatar)) {
    // CommUnity, Switchvox
    return ['avatar', userInfo?.avatar || null];
  } else if (isValidAvatarUrl(userInfo?.profile_picture)) {
    // CommUnity, Switchvox alternative
    return ['profile_picture', userInfo?.profile_picture || null];
  } else if (isValidAvatarUrl(userInfo?.image)) {
    // Generic fallback
    return ['image', userInfo?.image || null];
  } else if (isValidAvatarUrl(userInfo?.profile_image)) {
    // Additional fallback
    return ['profile_image', userInfo?.profile_image || null];
  } else if (isValidAvatarUrl(userInfo?.user_photo)) {
    // Additional fallback
    return ['user_photo', userInfo?.user_photo || null];
  } else if (isValidAvatarUrl(userInfo?.photoUrl)) {
    // Additional fallback
    return ['photoUrl', userInfo?.photoUrl || null];
  } else if (userEmail) {
    const blob = await getUserGravatar(userEmail);
    if (blob) {
      // NOTE: create object URL for the gravatar blob fetched above, for use with the <img> element.
      // NOTE: we revoke the blob with the <img> `onLoad` handler to make sure the memory is cleared.
      const objectURL = URL.createObjectURL(blob);
      return ['gravatar', objectURL];
    }

    return ['none', null];
  }
  // biome-ignore-end lint/style/noUselessElse: meh

  console.info('no profile image nor email for avatar');
  return ['none', null];
};

// NOTE: 1x1 transparent image as placeholder avatar while avatar is being loaded
const PLACEHOLDER_AVATAR =
  'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

const Header: React.FC<HeaderProps> = ({
  onLogout,
  onLogin,
  onOpenSettings,
  onOpenPersonalMeetings,
  userInfo: propUserInfo,
  isAuthenticated: propIsAuthenticated = false,
  useGlobalUser = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string | null>(PLACEHOLDER_AVATAR);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  // The visitor's platform cannot change mid-session, so resolve the list once.
  const appDownloadLinks = useMemo(() => getAppDownloadLinks(), []);

  // Use global user context if requested, otherwise use props
  const globalUserData = useUserForDisplay();

  const userInfo = useGlobalUser ? globalUserData.userInfo : propUserInfo;
  const isAuthenticated = useGlobalUser ? globalUserData.isAuthenticated : propIsAuthenticated;

  const userName = useGlobalUser
    ? globalUserData.displayName
    : userInfo?.name ||
      userInfo?.display_name ||
      userInfo?.full_name ||
      userInfo?.given_name ||
      userInfo?.preferred_username?.split('@')[0] ||
      userInfo?.upn?.split('@')[0] ||
      userInfo?.email?.split('@')[0] ||
      'Meet User';

  const userEmail = useGlobalUser
    ? globalUserData.email
    : userInfo?.email || userInfo?.preferred_username || userInfo?.upn;

  // TODO: deduplicate userName/userEmail/userInitials logic code
  // TODO: implemented here and on `src/contexts/UserContext.tsx`
  const userInitials = useMemo(() => {
    if (!userInfo) {
      return 'GU';
    }

    if (userInfo.given_name && userInfo.family_name) {
      return `${userInfo.given_name[0]}${userInfo.family_name[0]}`.toUpperCase();
    }

    if (userInfo.name) {
      const nameParts = userInfo.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      return nameParts[0].substring(0, 2).toUpperCase();
    }

    if (userName && userName !== 'Guest User') {
      return userName.substring(0, 2).toUpperCase();
    }

    return 'GU';
  }, [userInfo, userName]);

  console.log('userInitials', userInitials);

  useEffect(() => {
    let isMounted = true;

    void getUserAvatar(userInfo, userEmail).then(([avatarSource, userAvatar]) => {
      if (isMounted) {
        console.debug(
          'User avatar:',
          userAvatar ? `loaded from ${avatarSource} field: ${userAvatar}` : 'not defined',
        );
        setUserAvatar(userAvatar);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [userInfo, userEmail]);

  // Get full name for display
  const fullName =
    userInfo?.name ||
    userInfo?.full_name ||
    `${userInfo?.given_name || ''} ${userInfo?.family_name || ''}`.trim() ||
    userName;

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    authRedirectService.removeMeetingHashParams();
    onLogout?.();
  };

  const handleOpenSettings = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    onOpenSettings?.();
  };

  const handleOpenPersonalMeetings = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    onOpenPersonalMeetings?.();
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDownloadDropdownToggle = () => {
    setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
  };

  // Close dropdown and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !mobileMenuButtonRef.current?.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
      if (
        downloadDropdownRef.current &&
        !downloadDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDownloadDropdownOpen(false);
      }
    };

    if (isDropdownOpen || isMobileMenuOpen || isDownloadDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isMobileMenuOpen, isDownloadDropdownOpen]);

  return (
    <header className="header">
      {/* Left: Logo */}
      <div className="headerLeft">
        <div className="logo">
          <a
            href="https://sangoma.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Sangoma main website"
          >
            <picture>
              {/* Header only renders in standalone mode (hidden when hosted by
                  TeamHub), so the OS `prefers-color-scheme` is the correct — and
                  only — dark mode signal here; swap to the light wordmark SVG so
                  it stays legible against the dark page background. */}
              <source srcSet={headerLogoDark.src} media="(prefers-color-scheme: dark)" />
              <img
                src={headerLogo.src}
                width={headerLogo.width}
                height={headerLogo.height}
                alt="Sangoma Meet - Home"
                className="logoImage"
              />
            </picture>
          </a>
        </div>
      </div>

      {/* Center: Navigation Menu */}
      <nav className="headerNav" aria-label="Main navigation">
        <div ref={downloadDropdownRef} className="navDropdown">
          <button
            type="button"
            className="navItem"
            onClick={handleDownloadDropdownToggle}
            aria-haspopup="menu"
            aria-expanded={isDownloadDropdownOpen}
            aria-describedby="download-app-desc"
          >
            Download app
            <img
              src={chevronDownIcon.src}
              width={chevronDownIcon.width}
              height={chevronDownIcon.height}
              alt=""
              className={`navChevron ${isDownloadDropdownOpen ? 'open' : ''}`}
              aria-hidden="true"
            />
          </button>

          {isDownloadDropdownOpen && (
            <div className="navDropdownMenu" role="menu">
              {appDownloadLinks.map((link) => {
                const icon = APP_DOWNLOAD_ICONS[link.icon];
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="navDropdownItem"
                    role="menuitem"
                  >
                    <img
                      src={icon.src}
                      width={icon.width}
                      height={icon.height}
                      alt=""
                      className="navDropdownIcon"
                      aria-hidden="true"
                    />
                    {link.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div id="download-app-desc" className="sr-only">
          Access download options for mobile and desktop applications
        </div>

        {isAuthenticated && (
          <>
            <button
              type="button"
              className="navItem"
              onClick={handleOpenPersonalMeetings}
              aria-describedby="personal-meetings-desc"
            >
              Personal Meetings
            </button>
            <div id="personal-meetings-desc" className="sr-only">
              Manage your personal meeting rooms and settings
            </div>
            <button
              type="button"
              className="navItem"
              onClick={handleOpenSettings}
              aria-describedby="settings-desc"
            >
              Settings
            </button>
            <div id="settings-desc" className="sr-only">
              Open application settings and preferences
            </div>
          </>
        )}

        {/* Help & Support */}
        <a
          href="https://help.sangoma.com/community/s/"
          target="_blank"
          rel="noopener noreferrer"
          className="navItem"
          aria-describedby="help-desc"
        >
          Help & support
        </a>
        <div id="help-desc" className="sr-only">
          Access help documentation and customer support
        </div>
      </nav>

      {/* Right: User Actions */}
      <div className="headerRight">
        {isAuthenticated ? (
          /* Logged In User - Dropdown Menu */
          <div ref={dropdownRef} className="userDropdown">
            {/* User Avatar */}
            <div
              className={`userAvatar ${userAvatar ? 'hasImage' : ''}`}
              role="img"
              aria-label={`${fullName} profile picture`}
            >
              {userAvatar ? (
                <img
                  src={userAvatar}
                  onLoad={() => {
                    URL.revokeObjectURL(userAvatar);
                  }}
                  alt=""
                  className="userAvatarImage"
                  aria-hidden="true"
                  width={32}
                  height={32}
                  onError={(e) => {
                    // If image fails to load, hide it and show initial instead
                    console.warn('Header: Avatar image failed to load:', userAvatar);
                    e.currentTarget.style.display = 'none';
                    const nextSibling = e.currentTarget.nextSibling as HTMLElement;
                    if (nextSibling?.style) {
                      nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div className={`userAvatarInitial ${userAvatar ? 'hidden' : ''}`} aria-hidden="true">
                {userInitials}
              </div>
            </div>

            {/* User Name and Dropdown Trigger */}
            <button
              type="button"
              className="userInfo"
              onClick={handleDropdownToggle}
              aria-haspopup="menu"
              aria-expanded={isDropdownOpen}
              aria-label={`User menu for ${fullName}`}
              id="user-menu-button"
            >
              <span className="userName">{fullName}</span>
              <img
                src={chevronDownIcon.src}
                width={chevronDownIcon.width}
                height={chevronDownIcon.height}
                alt=""
                className={`chevronIcon ${isDropdownOpen ? 'open' : ''}`}
                aria-hidden="true"
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                className="dropdownMenu"
                role="menu"
                aria-labelledby="user-menu-button"
                aria-orientation="vertical"
              >
                {/* Logout */}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="dropdownItem"
                  role="menuitem"
                  aria-describedby="logout-menu-desc"
                >
                  <img
                    src={logoutIcon.src}
                    width={logoutIcon.width}
                    height={logoutIcon.height}
                    alt=""
                    className="buttonIcon"
                    aria-hidden="true"
                  />
                  Log out
                </button>
                <div id="logout-menu-desc" className="sr-only">
                  Sign out of your account
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Not Logged In - Login Button */
          <button
            type="button"
            onClick={onLogin}
            className="loginButton"
            aria-describedby="login-desc"
          >
            Log in
          </button>
        )}
        <div id="login-desc" className="sr-only">
          Sign in to access your account and meetings
        </div>

        {/* Mobile Menu Button */}
        <button
          ref={mobileMenuButtonRef}
          type="button"
          className="mobileMenuButton"
          onClick={handleMobileMenuToggle}
          aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <img
            src={burgerIcon.src}
            width={burgerIcon.width}
            height={burgerIcon.height}
            alt=""
            className="burgerIcon"
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <nav
          ref={mobileMenuRef}
          className="mobileMenu"
          id="mobile-menu"
          aria-label="Mobile navigation menu"
        >
          <div className="mobileMenuContent">
            {/* Navigation Items */}

            {isAuthenticated && (
              <>
                <button
                  type="button"
                  className="mobileNavItem"
                  onClick={handleOpenSettings}
                  aria-describedby="mobile-settings-desc"
                >
                  Settings
                </button>
                <div id="mobile-settings-desc" className="sr-only">
                  Open application settings and preferences
                </div>

                <button
                  type="button"
                  className="mobileNavItem"
                  onClick={handleOpenPersonalMeetings}
                  aria-describedby="mobile-personal-meetings-desc"
                >
                  Personal Meetings
                </button>
                <div id="mobile-personal-meetings-desc" className="sr-only">
                  Manage your personal meeting rooms and settings
                </div>
              </>
            )}

            {/* Download App Section */}
            <div className="mobileNavSection">
              <div className="mobileNavSectionTitle">Download app</div>
              {appDownloadLinks.map((link) => {
                const icon = APP_DOWNLOAD_ICONS[link.icon];
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobileNavSubItem"
                  >
                    <img
                      src={icon.src}
                      width={icon.width}
                      height={icon.height}
                      alt=""
                      className="mobileNavIcon"
                      aria-hidden="true"
                    />
                    {link.label}
                  </a>
                );
              })}
            </div>

            <a
              href="https://help.sangoma.com/community/s/"
              target="_blank"
              rel="noopener noreferrer"
              className="mobileNavItem"
              aria-describedby="mobile-help-desc"
            >
              Help & support
            </a>
            <div id="mobile-help-desc" className="sr-only">
              Access help documentation and customer support
            </div>

            {!isAuthenticated && (
              <button
                type="button"
                onClick={onLogin}
                className="mobileNavItem loginItem"
                aria-describedby="mobile-login-desc"
              >
                Log in
              </button>
            )}
            <div id="mobile-login-desc" className="sr-only">
              Sign in to access your account and meetings
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export { Header };
